import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Factory, Stethoscope, Building, ArrowRight, CheckCircle, MessageSquare, PhoneCall } from 'lucide-react';
import './GhaziabadLocation.css';
import FAQAccordion from './FAQAccordion';

const ghaziabadBelts = [
  'Sahibabad Industrial Area (Site 4 & 5)',
  'Loni Road Industrial Belt',
  'Mohan Nagar Industrial Zone',
  'Kavi Nagar & Bulandshahr Road Area',
  'Indirapuram & Vaishali Commercial Hubs',
  'Raj Nagar Extension & NH-9 Corridor'
];

const ghaziabadProcessDetailed = [
  { num: '01', title: 'On-Site Factory Visit', desc: 'Our senior consultants visit your factory or facility in Sahibabad, Loni, or Mohan Nagar for an operational audit.' },
  { num: '02', title: 'Technical Architecture & Scope', desc: 'Designing custom ERP weighbridge drivers, Tally accounting links, and database schemas.' },
  { num: '03', title: 'Agile Coding & On-Floor Hardware Testing', desc: 'Building custom software with live hardware testing at your plant premises.' },
  { num: '04', title: 'Security & Weighbridge Data Audit', desc: 'Testing weighbridge indicator anti-fraud logs, data encryption, and access controls.' },
  { num: '05', title: 'Operator Training & Plant Go-Live', desc: 'Hands-on operator training, legacy data migration, and full operational go-live.' },
  { num: '06', title: '24/7 SLA On-Site AMC Support', desc: 'SLA-backed 24/7 technical AMC support with emergency on-site visits across Ghaziabad.' }
];

const ghaziabadFaqsDetailed = [
  { q: 'Why choose Wayug Infotech for Ghaziabad software projects?', a: 'Wayug is located just 19km from Ghaziabad in Connaught Place, New Delhi. We specialize in custom manufacturing ERPs (steel mills, foundries) and provide immediate on-site visits across Sahibabad, Loni, and Mohan Nagar.' },
  { q: 'Can you build custom ERP software for steel rolling mills in Sahibabad?', a: 'Yes! We specialize in custom steel ERP development including weighbridge automation, billet-to-rebar yield calculation, furnace heat batch tracking, and Tally GST billing.' },
  { q: 'Do you offer free on-site consultations in Ghaziabad?', a: 'Absoltely. We provide free initial site visits across all industrial sites in Ghaziabad to understand your factory operations and provide a detailed quote.' },
  { q: 'Do we get 100% source code ownership for our Ghaziabad custom ERP or software?', a: 'Yes! 100% of the source code, repository access, and database belong entirely to your business upon completion.' },
  { q: 'Can your team integrate with local weighbridges in Sahibabad Site 4 & 5?', a: 'Yes! We interface directly with RS232/Ethernet digital weighbridge indicators to record accurate truck weights without manual operator tampering.' },
  { q: 'Do you sign an NDA before visiting our factory in Ghaziabad?', a: 'Yes, we sign mutual Non-Disclosure Agreements (NDAs) prior to inspecting plant machinery or reviewing operational documents.' },
  { q: 'What post-launch support is provided in Ghaziabad?', a: 'All projects include 30 days of free support, followed by optional SLA-backed Annual Maintenance Contracts (AMC) with on-site visit coverage.' }
];

export default function GhaziabadLocation() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20am%20located%20in%20Ghaziabad%20and%20would%20like%20to%20discuss%20a%20software%20project.";

  return (
    <section className="location-section">
      <div className="container">
        
        {/* Hero */}
        <motion.div 
          className="location-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="location-badge">
            <MapPin size={16} /> Ghaziabad &amp; Delhi NCR
          </div>
          <h1 className="location-title">
            Software Development Company in <span className="gradient-text">Ghaziabad</span>
          </h1>
          <p className="location-subtitle">
            Custom Software, Manufacturing ERP, Hospital Management Systems &amp; IT Solutions for businesses in Sahibabad, Loni, Indirapuram, Vaishali, and Mohan Nagar.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Get Free Site Visit &amp; Quote <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Contact Delhi NCR Team
            </a>
          </div>
        </motion.div>

        {/* Industrial Focus Grid */}
        <div className="location-grid">
          <div className="location-card">
            <Factory size={32} color="#f59e0b" style={{ marginBottom: '16px' }} />
            <h3>Steel &amp; Manufacturing ERP (Sahibabad &amp; Loni)</h3>
            <p>
              Custom ERP for Ghaziabad's 500+ steel rolling mills and factories. Track raw materials, heat batches, scrap weight, weighbridge data, and GST e-way bills effortlessly.
            </p>
          </div>

          <div className="location-card">
            <Stethoscope size={32} color="#06b6d4" style={{ marginBottom: '16px' }} />
            <h3>Hospital Management Software (Indirapuram &amp; Vaishali)</h3>
            <p>
              Mediway HMS for hospitals and nursing homes in Ghaziabad. ABDM-ready EMR, NABH compliance, OPD/IPD billing, pharmacy inventory, and Tally sync.
            </p>
          </div>

          <div className="location-card">
            <Building size={32} color="#10b981" style={{ marginBottom: '16px' }} />
            <h3>Real Estate CRM &amp; Distribution Software</h3>
            <p>
              Custom CRM for real estate builders and distributors across Raj Nagar Extension and NH-9 corridor. Auto-capture IndiaMart leads and automate WhatsApp follow-ups.
            </p>
          </div>
        </div>

        {/* 6-Step Delivery Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Ghaziabad Site Engagement Is Executed: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            Here is our on-site methodology for digitizing industrial plants and enterprises in Ghaziabad.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {ghaziabadProcessDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#f59e0b' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Ghaziabad Industrial Belts Covered */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', marginBottom: '80px' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '24px' }}>
            Ghaziabad Industrial Belts Covered With Free On-Site Consultations
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {ghaziabadBelts.map((b) => (
              <div key={b} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e5e5e5', fontSize: '0.95rem' }}>
                <CheckCircle size={16} color="#f59e0b" /> {b}
              </div>
            ))}
          </div>
        </div>

        {/* Extended FAQs */}
        <FAQAccordion 
          title="Detailed Ghaziabad Software Services FAQs" 
          faqs={ghaziabadFaqsDetailed} 
          accentColor="#f59e0b" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(6,182,212,0.15) 100%)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Located in Ghaziabad or Sahibabad? Request a Free Plant Visit</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Our engineering team provides on-site visits across Sahibabad, Loni, and Mohan Nagar. Get in touch with us today.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Request Site Visit via WhatsApp
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Send Inquiry
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
